
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Save, Send, Eye, Sparkles } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/hooks/use-toast';
import PreviewDialog from './parts/PreviewDialog';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import TitleGenerator from './ai-assistant/TitleGenerator';
import { useAIAssistant } from '@/contexts/ai-assistant/AIAssistantContext';
import { Card } from '@/components/ui/card';

interface EditorHeaderProps {
  title: string;
  setTitle: (title: string) => void;
  lastSaved: Date | null;
  isNew: boolean;
  onSave: () => Promise<any>;
  onPublish: () => Promise<void>;
  isSaving: boolean;
  isPublishing: boolean;
  content?: string;
  lead?: string;
  headerImageUrl?: string;
}

const EditorHeader: React.FC<EditorHeaderProps> = ({
  title,
  setTitle,
  lastSaved,
  isNew,
  onSave,
  onPublish,
  isSaving,
  isPublishing,
  content = '',
  lead,
  headerImageUrl
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isTitleDialogOpen, setIsTitleDialogOpen] = useState(false);
  const { toast } = useToast();
  const { isProcessing, activeFeature } = useAIAssistant();
  
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleOpenPreviewInNewTab = async () => {
    try {
      toast({
        title: "Preparing preview",
        description: "Saving your article before opening preview...",
      });
      
      await onSave();
      
      const previewKey = uuidv4();
      
      const previewData = {
        title,
        content,
        lead,
        headerImageUrl
      };
      
      console.log('Storing preview data:', previewData);
      sessionStorage.setItem(`article_preview_${previewKey}`, JSON.stringify(previewData));
      
      window.open(`/preview/${previewKey}`, '_blank');
    } catch (error) {
      console.error("Error preparing preview:", error);
      toast({
        title: "Preview Error",
        description: "There was a problem preparing the preview. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleTitleSelect = (selectedTitle: string) => {
    setTitle(selectedTitle);
    setIsTitleDialogOpen(false);
  };

  return (
    <>
      <div className="sticky top-0 z-50 bg-white dark:bg-gray-950 border-b border-border/40 shadow-sm py-5 px-5 bg-opacity-95 backdrop-blur-sm">
        <div className="flex items-center gap-2 max-w-[700px] mx-auto">
          <Input
            type="text"
            placeholder="Article Title"
            value={title}
            onChange={handleTitleChange}
            className="text-xl md:text-2xl font-medium border-0 shadow-none focus-visible:ring-transparent px-0 h-auto bg-transparent"
          />
          <Dialog open={isTitleDialogOpen} onOpenChange={setIsTitleDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-auto h-8 px-2 text-yellow-500 hover:text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30"
                disabled={isProcessing || !content}
              >
                {activeFeature === 'generateTitle' ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <TitleGenerator
                content={content}
                currentTitle={title}
                onSelectTitle={handleTitleSelect}
                onClose={() => setIsTitleDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
        
        {lastSaved && (
          <p className="text-xs text-muted-foreground mt-1 max-w-[700px] mx-auto">
            Last saved {formatDistanceToNow(lastSaved, { addSuffix: true })}
          </p>
        )}
      </div>
      
      {/* Action buttons - now sticky */}
      <div className="sticky top-[76px] z-40 px-5 py-4 bg-background/95 backdrop-blur-sm border-b border-border/40 shadow-sm">
        <div className="flex justify-end items-center gap-2 max-w-7xl mx-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenPreviewInNewTab}
            className="transition-all duration-200 hover:bg-accent/50"
          >
            <Eye className="mr-2 h-4 w-4" /> Preview
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={onSave}
            disabled={isSaving}
            className="transition-all duration-200 hover:bg-accent/50"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Save
              </>
            )}
          </Button>
          
          <Button
            variant="default"
            size="sm"
            onClick={onPublish}
            disabled={isPublishing || isNew}
            className="transition-all duration-200 shadow-sm hover:shadow"
          >
            {isPublishing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Publishing
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" /> Publish
              </>
            )}
          </Button>
        </div>
      </div>
      
      <PreviewDialog
        isOpen={isPreviewOpen}
        setIsOpen={setIsPreviewOpen}
        title={title}
        content={content}
        lead={lead}
        headerImageUrl={headerImageUrl}
      />
    </>
  );
};

export default EditorHeader;
